<template>
  <div class="push-plugin">
    <load-screen
      ref="loader"
      :startingText="$t('plugins.pushes.loaderText')"
    ></load-screen>
    <popup
      :title="$t('plugins.pushes.creationDialog.title')"
      ref="newEmissionDialog"
    >
      <div class="push-plugin__popup-content">
        <div class="push-plugin__popup-row">
          <sel
            class="push-plugin__popup-input"
            :label="$t('plugins.pushes.creationDialog.mode')"
            v-model="newEmission.mode"
            @change="getEstimatedRange"
          >
            <option :value="1">{{ $t("plugins.pushes.mode.basic") }}</option>
            <option :value="2">{{ $t("plugins.pushes.mode.otn") }}</option>
            <option :value="3">{{ $t("plugins.pushes.mode.last24h") }}</option>
          </sel>
          <in
            class="push-plugin__popup-input"
            :label="$t('plugins.pushes.creationDialog.name')"
            v-model="newEmission.name"
            required
          ></in>

          <sel
            class="push-plugin__popup-input"
            :label="$t('plugins.pushes.creationDialog.block')"
            v-model="newEmission.block_id"
          >
            <optgroup
              v-for="group in this.$store.state.flowGroups"
              :key="group.id"
              :label="group.name"
            >
              <option
                v-for="block in group.blocks"
                :key="block.id"
                :value="block.id"
              >
                {{ block.name }}
              </option>
            </optgroup>
          </sel>
        </div>
        <div class="push-plugin__popup-row">
          <sel
            class="push-plugin__popup-input push-plugin__popup-input--half"
            v-show="newEmission.mode == 2"
            :label="$t('plugins.pushes.creationDialog.otnName')"
            v-model="newEmission.otn"
            @change="getEstimatedRange"
          >
            <option
              :value="val.name"
              v-for="(val, i) in availableOtns"
              :key="i"
            >
              {{ val.name }}
            </option>
          </sel>
          <sel
            class="push-plugin__popup-input push-plugin__popup-input--half"
            v-show="newEmission.mode == 1"
            :label="$t('plugins.pushes.creationDialog.tag')"
            v-model="newEmission.tag"
          >
            <option :value="val" v-for="(val, key) in tags" :key="key">
              {{ key }}
            </option>
          </sel>
        </div>
        <div class="push-plugin__popup-filters">
          <div class="push-plugin__popup-row">
            <sel
              class="push-plugin__popup-input"
              :label="$t('plugins.pushes.creationDialog.gender')"
              v-model="newEmission.target.gender"
              @change="getEstimatedRange"
            >
              <option value="all">
                {{ $t("plugins.pushes.creationDialog.allUsers") }}
              </option>
              <option value="male">
                {{ $t("plugins.pushes.creationDialog.males") }}
              </option>
              <option value="female">
                {{ $t("plugins.pushes.creationDialog.females") }}
              </option>
            </sel>
          </div>
          <div class="push-plugin__popup-row">
            <sel
              class="push-plugin__popup-input push-plugin__popup-input--half"
              :label="$t('plugins.pushes.creationDialog.setModeIncluded')"
              v-model="newEmission.target.includeMode"
              @change="getEstimatedRange"
            >
              <option value="addition">
                {{ $t("plugins.pushes.creationDialog.addition") }}
              </option>
              <option value="product">
                {{ $t("plugins.pushes.creationDialog.product") }}
              </option>
            </sel>
            <sel
              class="push-plugin__popup-input push-plugin__popup-input--half"
              :label="$t('plugins.pushes.creationDialog.setModeExcluded')"
              v-model="newEmission.target.excludeMode"
              @change="getEstimatedRange"
            >
              <option value="addition">
                {{ $t("plugins.pushes.creationDialog.addition") }}
              </option>
              <option value="product">
                {{ $t("plugins.pushes.creationDialog.product") }}
              </option>
            </sel>
          </div>
          <div class="push-plugin__popup-row">
            <div
              class="push-plugin__popup-multiselect push-plugin__popup-input push-plugin__popup-input--half"
            >
              <div class="push-plugin__multiselect-label">
                {{ $t("plugins.pushes.creationDialog.included") }}
              </div>
              <multiselect
                class="push-plugin__multiselect push-plugin__multiselect--included"
                open-direction="top"
                trackBy="id"
                label="name"
                placeholder
                deselectLabel
                selectLabel
                v-model="tempIncluded"
                id="include"
                :hideSelected="true"
                :max-height="240"
                :close-on-select="false"
                :multiple="true"
                :options="availableToInclude"
                @input="multiselectChange"
              ></multiselect>
            </div>
            <div
              class="push-plugin__popup-multiselect push-plugin__popup-input push-plugin__popup-input--half"
            >
              <div class="push-plugin__multiselect-label">
                {{ $t("plugins.pushes.creationDialog.excluded") }}
              </div>
              <multiselect
                class="push-plugin__multiselect push-plugin__multiselect--excluded"
                open-direction="top"
                trackBy="id"
                label="name"
                placeholder
                deselectLabel
                selectLabel
                v-model="tempExcluded"
                id="exclude"
                :hideSelected="true"
                :max-height="240"
                :close-on-select="false"
                :multiple="true"
                :options="availableToExclude"
                @input="multiselectChange"
              ></multiselect>
            </div>
          </div>

          <div class="push-plugin__popup-row">
            <div class="push-plugin__popup-input">
              <div class="push-plugin__checkbox-label">
                {{ $t("plugins.pushes.creationDialog.plannedTitle") }}
              </div>
              <div class="push-plugin__popup-checkbox">
                <label>
                  <input
                    type="checkbox"
                    v-model="newEmission.schedule.isEnabled"
                  />
                  {{ $t("plugins.pushes.creationDialog.plannedLabel") }}
                </label>
              </div>
            </div>

            <in
              type="date"
              class="push-plugin__popup-input"
              :label="$t('plugins.pushes.creationDialog.plannedDate')"
              v-model="newEmission.schedule.date"
              v-if="newEmission.schedule.isEnabled"
            ></in>

            <sel
              class="push-plugin__popup-input"
              :label="$t('plugins.pushes.creationDialog.plannedTime')"
              v-model="newEmission.schedule.time"
              v-if="newEmission.schedule.isEnabled"
            >
              <option v-for="hour in fullHours" :key="hour" :value="hour">
                {{ hour }}
              </option>
            </sel>
          </div>

          <div class="push-plugin__popup-range">
            {{ $t("plugins.pushes.creationDialog.range") }}:
            <span
              class="push-plugin__popup-range-value"
              v-if="estimatedRange !== null"
              >{{ estimatedRange }}</span
            >
            <span class="push-plugin__popup-range-value" v-else>-</span>
          </div>
        </div>
      </div>
      <template v-slot:buttons>
        <btn
          icon="times"
          color="red"
          @click="$refs.newEmissionDialog.close()"
          >{{ $t("btn.cancel") }}</btn
        >
        <btn icon="save" @click="createEmission" color="green">{{
          $t("btn.create")
        }}</btn>
      </template>
    </popup>

    <popup :title="$t('plugins.pushes.delDialog.title')" ref="delDialog">
      <div class="push-plugin__popup-content">
        {{ $t("plugins.pushes.delDialog.text") }}
        <b>{{ emissionToDelete.name }}</b
        >?
      </div>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.delDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn
          icon="trash"
          @click="deleteEmission(emissionToDelete.id)"
          color="red"
          >{{ $t("btn.delete") }}</btn
        >
      </template>
    </popup>

    <popup :title="$t('plugins.pushes.cancelDialog.title')" ref="cancelDialog">
      <div class="push-plugin__popup-content">
        {{ $t("plugins.pushes.cancelDialog.text") }}
        <b>{{ emissionToDelete.name }}</b> (id {{ emissionToDelete.id }})?
      </div>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.cancelDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn
          icon="trash"
          @click="cancelEmission(emissionToDelete.id)"
          color="red"
          >{{ $t("btn.delete") }}</btn
        >
      </template>
    </popup>

    <popup :title="$t('plugins.pushes.infoDialog.title')" ref="infoDialog">
      <div class="push-plugin__details">
        <div class="push-plugin__details-column">
          <in
            :label="$t('plugins.pushes.tableHead.name')"
            disabled
            class="push-plugin__info-row"
            :value="detailedEmission.name"
          ></in>
          <in
            :label="$t('plugins.pushes.tableHead.startedAt')"
            disabled
            class="push-plugin__info-row"
            :value="formDate(detailedEmission.started_at)"
          ></in>
          <in
            :label="$t('plugins.pushes.tableHead.time')"
            disabled
            :value="
              detailedEmission.started_at && detailedEmission.finished_at
                ? (
                    (+new Date(detailedEmission.finished_at) -
                      +new Date(detailedEmission.started_at)) /
                    1000
                  ).toFixed(2)
                : '-'
            "
            class="push-plugin__info-row"
          ></in>
          <in
            :label="$t('plugins.pushes.tableHead.block')"
            disabled
            class="push-plugin__info-row"
            :value="
              detailedEmission.block_id
                ? findFlowBlock(detailedEmission.block_id).name
                : '-'
            "
          ></in>
          <in
            :label="$t('plugins.pushes.tableHead.tag')"
            disabled
            class="push-plugin__info-row"
            :value="findTagName(detailedEmission.tag)"
            v-if="detailedEmission.mode === 1"
          ></in>
          <in
            :label="$t('plugins.pushes.creationDialog.otnName')"
            disabled
            class="push-plugin__info-row"
            :value="detailedEmission.otn"
            v-if="detailedEmission.mode === 2"
          ></in>
          <in
            :label="$t('plugins.pushes.tableHead.targets')"
            disabled
            class="push-plugin__info-row"
            :value="detailedEmission.targets || '-'"
          ></in>
          <in
            :label="$t('plugins.pushes.tableHead.successes')"
            disabled
            class="push-plugin__info-row"
            :value="
              detailedEmission.targets && detailedEmission.successes
                ? (
                    (detailedEmission.successes / detailedEmission.targets) *
                    100
                  ).toFixed(2) + '%'
                : '-'
            "
          ></in>
          <in
            :label="$t('plugins.pushes.tableHead.seenBy')"
            disabled
            class="push-plugin__info-row"
            :value="seenStr(detailedEmission)"
          ></in>
        </div>
        <div class="push-plugin__details-column">
          <h4>{{ $t("plugins.pushes.tableHead.dump") }}</h4>
          <div class="push-plugin__json-dump">
            <pre>{{
              detailedEmission.message_dump &&
              detailedEmission.message_dump.dump
                ? JSON.stringify(detailedEmission.message_dump.dump, null, 2)
                : ""
            }}</pre>
          </div>
        </div>
      </div>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.infoDialog.close()">{{
          $t("btn.close")
        }}</btn>
      </template>
    </popup>

    <popup :title="$t('plugins.pushes.pushDialog.title')" ref="pushDialog">
      {{ $t("plugins.pushes.pushDialog.text1") }}
      <b>{{ emissionToPush.name }}</b>
      {{ $t("plugins.pushes.pushDialog.text2") }}
      <b>{{ emissionToPush.group }}</b
      >{{ $t("plugins.pushes.pushDialog.text3") }}
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.pushDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn icon="bullhorn" @click="push(emissionToPush.id)" color="green">{{
          $t("plugins.pushes.pushDialog.push")
        }}</btn>
      </template>
    </popup>

    <div class="push-plugin__top-buttons">
      <btn
        @click="$refs.newEmissionDialog.open()"
        class="push-plugin__button"
        icon="plus"
        >{{ $t("plugins.pushes.button.newEmission") }}</btn
      >
    </div>

    <div class="push-plugin__main">
      <div class="push-plugin__list">
        <div class="push-plugin__list-head">
          <div class="push-plugin__row-element" style="width: 20%">
            {{ $t("plugins.pushes.tableHead.name") }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            {{ $t("plugins.pushes.creationDialog.mode") }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            {{ $t("plugins.pushes.tableHead.startedAt") }}
          </div>
          <div class="push-plugin__row-element" style="width: 20%">
            {{ $t("plugins.pushes.tableHead.group") }}
          </div>
          <div class="push-plugin__row-element" style="width: 20%">
            {{ $t("plugins.pushes.tableHead.block") }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            {{ $t("plugins.pushes.tableHead.scheduled") }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            {{ $t("plugins.pushes.tableHead.actions") }}
          </div>
        </div>
        <div
          v-for="emission of emissions"
          :key="emission.id"
          class="push-plugin__row"
        >
          <div class="push-plugin__row-element" style="width: 20%">
            {{ emission.name }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            {{ $t("plugins.pushes.mode." + modes[emission.mode - 1]) }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            {{ formDate(emission.started_at) }}
          </div>
          <div class="push-plugin__row-element" style="width: 20%">
            {{ emission.targetString || "-" }}
          </div>
          <div class="push-plugin__row-element" style="width: 20%">
            {{
              emission.block_id ? findFlowBlock(emission.block_id).name : "-"
            }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            {{
              emission.scheduled_at
                ? new Date(emission.scheduled_at).toLocaleString("pl-PL", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-"
            }}
          </div>
          <div class="push-plugin__row-element" style="width: 10%">
            <fai
              @click="openCancelDialog(emission)"
              v-if="emission.scheduled_at && emission.state === 'created'"
              class="push-plugin__row-icon"
              icon="times"
            ></fai>
            <fai
              @click="openPushDialog(emission)"
              v-if="emission.state === 'created'"
              class="push-plugin__row-icon"
              icon="bullhorn"
            ></fai>
            <fai
              @click="openDeleteDialog(emission)"
              v-if="emission.state === 'created'"
              class="push-plugin__row-icon"
              icon="trash"
            ></fai>

            <fai
              v-if="emission.state === 'finished'"
              class="push-plugin__row-icon"
              icon="info"
              @click="openDetailDialog(emission)"
            ></fai>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Multiselect from "../files/vue-multiselect.min.js";

export default {
  components: { Multiselect },
  data() {
    return {
      newEmission: {
        name: "",
        tag: 1,
        block_id: 0,
        mode: 1,
        otn: "",
        target: {
          gender: "all",
          includeMode: "addition",
          excludeMode: "addition",
          include: [],
          exclude: [],
        },
        schedule: {
          isEnabled: false,
          time: "00:00",
          date: new Date()
            .toLocaleDateString("pl-PL", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
            .split(".")
            .reverse()
            .join("-"),
        },
      },
      tags: {
        CONFIRMED_EVENT_UPDATE: 1,
        POST_PURCHASE_UPDATE: 2,
        ACCOUNT_UPDATE: 3,
        NON_PROMOTIONAL_SUBSCRIPTION: 4,
      },
      availableOtns: [],
      emissions: [],
      emissionToDelete: {
        id: null,
        name: null,
      },
      emissionToPush: {
        id: null,
        name: null,
        group: null,
      },
      detailedEmission: {
        id: null,
        mode: null,
        name: null,
        started_at: null,
        finished_at: null,
        group_id: null,
        block_id: null,
        fallback_group_name: null,
        message_dump: null,
        tag: null,
        targets: null,
        successes: null,
        state: null,
        unseen_count: null,
      },
      modes: ["basic", "otn", "last24h"],
      tempIncluded: [],
      tempExcluded: [],
      availableToInclude: [],
      availableToExclude: [],
      estimatedRange: null,
      mappedUsersGroups: {},
    };
  },
  computed: {
    fullHours() {
      return Array.from(
        { length: 24 },
        (_, i) => `${i.toString().padStart(2, "0")}:00`
      );
    },
  },
  methods: {
    clearCreationPopup() {
      this.newEmission = {
        name: "",
        tag: 0,
        block_id: 0,
        target: {
          gender: "all",
          includeMode: "addition",
          excludeMode: "addition",
          include: [],
          exclude: [],
        },
        schedule: {
          isEnabled: false,
          time: "00:00",
          date: new Date().toISOString().split("T")[0],
        },
      };
      this.tempIncluded = [];
      this.tempExcluded = [];
    },

    async createEmission() {
      try {
        const createReq = await axios.post(
          "/api/plugin/pushes/emission",
          this.newEmission
        );
        this.createTargetStrings([createReq.data.created]);
        this.emissions.unshift(createReq.data.created);
        this.clearCreationPopup();
        this.$refs.newEmissionDialog.close();
        this.notify("success", "plugins.pushes.success.create");
      } catch (e) {
        console.error(e);
        this.$refs.newEmissionDialog.close();
        this.notify("error", "plugins.pushes.error.create");
        console.log(e);
      }
    },

    findUserGroup(id) {
      let g = null;
      for (const group of this.$store.state.usersGroups) {
        if (group.id === id) {
          g = group;
          break;
        }
      }

      return g;
    },

    findFlowBlock(id) {
      let b = null;
      const blocks = this.$store.getters.flowBlocks;
      for (const block in blocks) {
        if (blocks[block].id === id) {
          b = blocks[block];
          break;
        }
      }
      return b;
    },

    findTagName(id) {
      let t = null;
      for (const tag in this.tags) {
        if (this.tags[tag] === id) {
          t = tag;
          break;
        }
      }
      if (!t) return null;
      return t.replace(/_/g, " ");
    },

    openDeleteDialog(item) {
      this.emissionToDelete = {
        name: item.name,
        id: item.id,
      };
      this.$refs.delDialog.open();
    },

    async deleteEmission(id) {
      try {
        await axios.delete("/api/plugin/pushes/emission", { data: { id } });
        for (let i = 0; i < this.emissions.length; i++) {
          if (this.emissions[i].id === id) {
            this.$delete(this.emissions, i);
          }
        }
        this.notify("success", "plugins.pushes.success.delete");
        this.$refs.delDialog.close();
      } catch (e) {
        console.error(e);
        this.notify("error", "plugins.pushes.error.delete");
        this.$refs.delDialog.close();
      }
    },

    openCancelDialog(item) {
      this.emissionToDelete = {
        name: item.name,
        id: item.id,
      };
      this.$refs.cancelDialog.open();
    },

    async cancelEmission(id) {
      try {
        const cancelReq = await axios.put("/api/plugin/pushes/emission", {
          data: { id },
        });
        for (let i = 0; i < this.emissions.length; i++) {
          if (this.emissions[i].id === cancelReq.data.actual.id) {
            this.$set(this.emissions, i, cancelReq.data.actual);
            break;
          }
        }
        this.notify("success", "plugins.pushes.success.cancel");
        this.$refs.cancelDialog.close();
      } catch (e) {
        console.error(e);
        this.notify("error", "plugins.pushes.error.cancel");
        this.$refs.cancelDialog.close();
      }
    },

    openPushDialog(item) {
      this.emissionToPush = {
        name: item.name,
        id: item.id,
        group: item.targetString || "-",
      };
      this.$refs.pushDialog.open();
    },

    openDetailDialog(emission) {
      this.detailedEmission = emission;
      this.$refs.infoDialog.open();
    },

    async push(id) {
      try {
        this.$refs.pushDialog.close();
        this.$refs.loader.show(30000);
        const pushReq = await axios.post("/api/plugin/pushes/execute", { id });
        for (let i = 0; i < this.emissions.length; i++) {
          if (this.emissions[i].id === pushReq.data.actual.id) {
            this.$set(this.emissions, i, pushReq.data.actual);
            break;
          }
        }
        this.$refs.loader.hide();
        this.notify("success", "plugins.pushes.success.push");
      } catch (e) {
        this.$refs.loader.hide();
        console.error(e);
        this.notify("error", "plugins.pushes.error.push");
      }
    },

    prependZeroIfNeeded(s) {
      if (s.length === 1) s = "0" + s;
      return s;
    },

    seenStr(detailedEmission) {
      const s = detailedEmission.successes
        ? (
            ((detailedEmission.successes - detailedEmission.unseen_count) /
              detailedEmission.successes) *
            100
          ).toFixed(2) + "%"
        : "-";
      if (isNaN(s.replace("%", ""))) return "-";
      return s;
    },

    formDate(d) {
      if (!d) return "-";
      d = new Date(d);
      const day = this.prependZeroIfNeeded(d.getDate().toString());
      const month = this.prependZeroIfNeeded((d.getMonth() + 1).toString());
      const year = d.getFullYear();

      const h = this.prependZeroIfNeeded(d.getHours().toString());
      const m = this.prependZeroIfNeeded(d.getMinutes().toString());

      return `${day}.${month}.${year}, ${h}:${m}`;
    },

    multiselectChange(val, id) {
      this.getEstimatedRange();
      this.newEmission.target[id] = val.map((g) => g.id);
    },

    createTargetStrings(emissions) {
      for (const emission of emissions) {
        let s = "";
        if (emission.gender === "all") s = "";
        else {
          s = emission.gender + "s, ";
          s = s.charAt(0).toUpperCase() + s.slice(1);
        }

        const included = [];
        const excluded = [];
        const iSymbol = emission.include_mode === "addition" ? " ∪ " : " ∩ ";
        const eSymbol = emission.exclude_mode === "addition" ? " ∪ " : " ∩ ";

        for (const group of emission.targetedGroups) {
          if (group.type === "e") excluded.push(group.group_id);
          else if (group.type === "i") included.push(group.group_id);
        }

        for (let i = 0; i < included.length; i++) {
          s +=
            included[i] !== null
              ? this.mappedUsersGroups[included[i]].name
              : this.$t("plugins.pushes.creationDialog.allUsers");
          if (i + 1 !== included.length) s += iSymbol;
        }

        if (!excluded.length) {
          emission.targetString = s;
          continue;
        }

        if (included.length > 1) s = "(" + s + ")";
        s += " ∖ ";
        let excludedStr = "";
        for (let i = 0; i < excluded.length; i++) {
          excludedStr +=
            excluded[i] !== null
              ? this.mappedUsersGroups[excluded[i]].name
              : this.$t("plugins.pushes.creationDialog.allUsers");
          if (i + 1 !== excluded.length) excludedStr += eSymbol;
        }
        if (excluded.length > 1) excludedStr = "(" + excludedStr + ")";
        s += excludedStr;
        emission.targetString = s;
      }
    },

    mapUsersGroups() {
      for (const group of this.$store.state.usersGroups) {
        this.mappedUsersGroups[group.id] = group;
      }
    },

    async getEstimatedRange() {
      try {
        this.estimatedRange = null;
        const req = await axios.post(
          "/api/plugin/pushes/range",
          this.newEmission
        );
        this.estimatedRange = req.data.count;
      } catch (e) {
        console.error(e);
        this.notify("error", "plugins.pushes.error.load");
      }
    },
  },

  async created() {
    try {
      await this.$store.dispatch("loadFlowData", this);
      const dataReq = await axios.get("/api/plugin/pushes/list");
      const otnsReq = await axios.get("/api/plugin/pushes/list-otns");
      this.mapUsersGroups();
      this.createTargetStrings(dataReq.data.emissions);
      this.emissions = dataReq.data.emissions;
      this.availableOtns = otnsReq.data.otns;
      this.availableToInclude = [
        { id: 0, name: this.$t("plugins.pushes.creationDialog.allUsers") },
        ...this.$store.state.usersGroups,
      ];
      this.availableToExclude = this.$store.state.usersGroups;
    } catch (e) {
      console.error(e);
      this.notify("error", "plugins.pushes.error.load");
    }
  },
};
</script>

<style src="../files/vue-multiselect.min.css"></style>
<style lang="scss">
.push-plugin {
  &__top-buttons {
    display: flex;
    justify-content: flex-end;
    padding: 15px 20px 0px 20px;
  }

  &__popup-content {
    width: 700px;
    margin: 0 auto;
    text-align: center;
  }

  &__popup-row {
    display: flex;
    margin-bottom: 12px;
  }

  &__popup-input {
    width: 49%;
    margin: 0 4px;
    width: calc(33% - 4px);

    &--half {
      width: calc(50% - 4px);
    }
  }

  &__popup-filters {
    margin-top: 30px;
  }

  &__checkbox-label {
    @include labelStyle();
  }

  &__popup-checkbox {
    font-family: "Lato";
    font-weight: 300;
    margin-top: 7px;
  }

  &__multiselect {
    .multiselect__select {
      height: 31px;
      padding-right: 0;
      margin-right: -3px;
      display: none;
    }

    .multiselect__tags {
      @include inputStyle(5px);
      min-height: 31px;
      width: 100%;
      padding-bottom: 3px;
    }

    .multiselect__tag {
      margin: 1px 3px;
      background: $primary;
    }

    .multiselect__placeholder {
      display: none;
    }

    .multiselect__option--highlight {
      background: $primary;

      &:after {
        background: $primary;
      }
    }

    &--included {
      .multiselect__tag {
        background: $primary;

        &-icon:hover {
          background: $primaryHover;
        }
      }
    }

    &--excluded {
      .multiselect__tag {
        background: $danger;

        &-icon:hover {
          background: $dangerHover;
        }
      }
    }
  }

  &__multiselect-label {
    @include labelStyle();
  }

  &__popup-range {
    padding: 0 0 0 5px;
    text-align: center;
    font-family: "Lato";
    font-weight: 300;
    font-size: 10px;
  }

  &__popup-range-value {
    margin-left: 1px;
    font-weight: 700;
  }

  &__main {
    padding-top: 30px;
    width: 90%;
    margin: 0 auto;
  }

  &__list {
    width: 100%;
    height: 80vh;
    overflow: auto;
    position: relative;
  }

  &__list-head {
    position: sticky;
    background-color: #f2f2f2;
    top: 0;
    width: calc(100% - 20px);
    display: flex;
    justify-content: space-around;
    align-items: center;
    border-bottom: 2px solid $borderGrey;
    padding: 0px 10px 10px 10px;
    font-weight: 700;
    word-wrap: break-word;
  }

  &__row {
    display: flex;
    justify-content: space-around;
    align-items: center;
    font-size: 12px;
    padding: 18px 10px;

    &:not(:last-of-type) {
      border-bottom: 1px solid $borderGrey;
    }
  }

  &__row-icon {
    color: #b8b8b8;
    cursor: pointer;
    margin: 0 8px;
    font-size: 1.1em;

    &:hover {
      color: #9b9b9b;
    }
  }

  &__details {
    overflow: auto;
    max-height: 70%;
    display: flex;
    width: 65vw;
    max-width: 1000px;
    min-width: 700px;
    justify-content: space-between;
  }

  &__details-column {
    &:first-of-type {
      width: 38%;
    }
    &:last-of-type {
      width: 60%;
    }
  }

  &__info-row {
    &:not(:last-of-type) {
      margin-bottom: 8px;
    }
  }

  &__json-dump {
    background-color: #f7f7f7;
    padding: 1px 12px;
    border-radius: 4px;
    overflow: auto;
    max-height: 443px;
  }
}
</style>
